import { FC, FormEventHandler, useEffect, useState } from "react";
import styles from "./add-work-completed.module.scss";
import { Modal } from "../../../../../ui/modal/Modal";
import { Button } from "../../../../../ui/button/Button";
import { z } from "zod";
import { Snackbar } from "../../../../../ui/snackbar/Snackbar";
import { useAddWorkCompletedMutation } from "../worksCompletedApi";
import { useGetFlightQuery } from "../../flight/flightApi";
import { HHMM2min } from "../../../../../helpers/time";
import { useGetLandCategoriesQuery } from "../../../../../api/landCategoriesApi";
import { useGetAirRoutesOptionsQuery } from "../../../../../api/routesApi";

type Props = {
  close: () => void;
  flight_work_id: number;
};

type State = {
  errors?: {
    contract_work_id?: string[];
    time?: string[];
    route_id?: string[];
    fire_card_id?: string[];
    discharge_count?: string[];
    discharge_volume?: string[];
    influence_count?: string[];
    land_category_code?: string[];
    jumps?: string[];
    descent?: string[];
  };
  message?: string | null;
};

const FormSchema = z.object({
  contract_work_id: z.coerce
    .number({
      message: "Выберите из списка",
    })
    .positive({
      message: "Выберите из списка",
    }),
  time: z.string().transform((val) => {
    return HHMM2min(val);
  }),
  route_id: z.coerce.number().nullable(),
  fire_card_id: z.coerce.number().nullable(),
  discharge_count: z.coerce.number(),
  discharge_volume: z.coerce.number(),
  influence_count: z.coerce.number(),
  land_category_code: z.coerce
    .number({
      message: "Выберите из списка",
    })
    .positive({
      message: "Выберите из списка",
    }),
  jumps: z.coerce.number(),
  descent: z.coerce.number(),
});

type OptionType = {
  id: number;
  name: string;
};

const Form: FC<Props> = ({ close, flight_work_id }) => {
  const initialState: State = { message: null, errors: {} };
  const [state, setState] = useState<State>(initialState);
  const [isUXOpen, setIsUXOpen] = useState(true);
  const [workTypeOptions, setWorkTypeOptions] = useState<OptionType[]>([]);
  const [add, { isError, isLoading, isSuccess }] =
    useAddWorkCompletedMutation();

  const { data: flight, isError: isFlightError } = useGetFlightQuery(
    Number(flight_work_id)
  );

  const { data: landCategories } = useGetLandCategoriesQuery();
  const { data: routes, isError: isRoutesError } =
    useGetAirRoutesOptionsQuery();

  const submit: FormEventHandler = (e) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    setState(initialState);
    setIsUXOpen(true);

    const validatedFields = FormSchema.safeParse({
      contract_work_id: formData.get("contract_work_id"),
      time: formData.get("time"),
      fire_card_id: formData.get("fire_card_id"),
      discharge_count: formData.get("discharge_count"),
      discharge_volume: formData.get("discharge_volume"),
      influence_count: formData.get("influence_count"),
      land_category_code: formData.get("land_category_code"),
      descent: formData.get("descent"),
      jumps: formData.get("jumps"),
      route_id: formData.get("route_id"),
    });

    if (!validatedFields.success) {
      setState({
        errors: validatedFields.error.flatten().fieldErrors,
        message: "Не удалось сохранить форму. Заполните все поля.",
      });
      return;
    }

    const {
      contract_work_id,
      time,
      fire_card_id,
      discharge_count,
      discharge_volume,
      influence_count,
      land_category_code,
      descent,
      jumps,
      route_id,
    } = validatedFields.data;

    /**
     * Получать
     * fire_card_id
     */
    add({
      flight_work_id,
      contract_work_id,
      time,
      fire_card_id: null,
      discharge_count,
      discharge_volume,
      influence_count,
      land_category_code,
      descent,
      jumps,
      route_id,
    });
  };

  useEffect(() => {
    if (!isSuccess) return;
    setTimeout(() => {
      close();
    }, 1500);
  }, [isSuccess]);

  useEffect(() => {
    if (!flight) return;
    const typeOptions = flight.works.map((work) => ({
      id: work.id,
      name: work.work,
    }));
    setWorkTypeOptions(typeOptions);
  }, [flight]);

  return (
    <form className={styles.form} onSubmit={submit}>
      <div className={styles.title}>Добавление выполненной работы</div>
      <div className={styles.row}>
        <label htmlFor="contract_work_id" className={styles.label}>
          Вид работ
        </label>
        <select className={styles.select} name="contract_work_id">
          <option className={styles.option}>Выберите</option>
          {workTypeOptions.map((item) => (
            <option key={item.id} className={styles.option} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
        <div id="contract_work_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.contract_work_id &&
            state.errors.contract_work_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="time">Затраченное время</label>
        <input
          className={styles.input}
          name="time"
          placeholder="Затраченное время"
          type="time"
          defaultValue="00:00"
        />
        <div id="time-error" aria-live="polite" aria-atomic="true">
          {state.errors?.time &&
            state.errors.time.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="route_id" className={styles.label}>
          Номер маршрута
        </label>
        {routes && (
          <select className={styles.select} name="route_id">
            <option className={styles.option}>Выберите</option>
            {routes.map((item) => (
              <option key={item.id} className={styles.option} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        )}
        <div id="route_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.route_id &&
            state.errors.route_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="fire_card_id">Лесной пожар</label>
        <input
          className={styles.input}
          name="fire_card_id"
          placeholder="Лесной пожар"
        />
        <div id="fire_card_id-error" aria-live="polite" aria-atomic="true">
          {state.errors?.fire_card_id &&
            state.errors.fire_card_id.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="discharge_count">Количество сливов</label>
        <input
          className={styles.input}
          name="discharge_count"
          placeholder="Количество сливов"
          type="number"
        />
        <div id="discharge_count-error" aria-live="polite" aria-atomic="true">
          {state.errors?.discharge_count &&
            state.errors.discharge_count.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="discharge_volume">Объем сливов</label>
        <input
          className={styles.input}
          name="discharge_volume"
          placeholder="Объем сливов"
          type="number"
        />
        <div id="discharge_volume-error" aria-live="polite" aria-atomic="true">
          {state.errors?.discharge_volume &&
            state.errors.discharge_volume.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="influence_count">Воздействия</label>
        <input
          className={styles.input}
          name="influence_count"
          placeholder="Воздействия"
          type="number"
        />
        <div id="influence_count-error" aria-live="polite" aria-atomic="true">
          {state.errors?.influence_count &&
            state.errors.influence_count.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="land_category_code" className={styles.label}>
          Принадлежность лесов
        </label>
        {landCategories && (
          <select className={styles.select} name="land_category_code">
            <option className={styles.option}>Выберите</option>
            {landCategories.map((item) => (
              <option key={item.id} className={styles.option} value={item.id}>
                {item.title}
              </option>
            ))}
          </select>
        )}
        <div
          id="land_category_code-error"
          aria-live="polite"
          aria-atomic="true"
        >
          {state.errors?.land_category_code &&
            state.errors.land_category_code.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="jumps">Прыжки</label>
        <input
          className={styles.input}
          name="jumps"
          placeholder="Прыжки"
          type="number"
        />
        <div id="jumps-error" aria-live="polite" aria-atomic="true">
          {state.errors?.jumps &&
            state.errors.jumps.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.row}>
        <label htmlFor="descent">Спуски</label>
        <input
          className={styles.input}
          name="descent"
          placeholder="Спуски"
          type="number"
        />
        <div id="descent-error" aria-live="polite" aria-atomic="true">
          {state.errors?.descent &&
            state.errors.descent.map((error: string) => (
              <p className={styles.error} key={error}>
                {error}
              </p>
            ))}
        </div>
      </div>
      <div className={styles.actions}>
        <Button type="reset" onClick={() => setState(initialState)}>
          Очистить
        </Button>
        <Button type="submit" isProcessing={isLoading} isDisabled={isLoading}>
          Сохранить
        </Button>
      </div>
      {state.message && isUXOpen && (
        <Snackbar
          isOpen={Boolean(state.message)}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          {state.message}
        </Snackbar>
      )}
      {isSuccess && (
        <Snackbar isOpen={isSuccess} className="success" onClose={close}>
          Изменения сохранены
        </Snackbar>
      )}
      {isError && isUXOpen && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Ошибка сохранения. Попробуйте позже
        </Snackbar>
      )}
      {(isFlightError || isRoutesError) && (
        <Snackbar
          isOpen={isError}
          className="error"
          onClose={() => setIsUXOpen(false)}
        >
          Не удалось загрузить данные. Попробуйте позже
        </Snackbar>
      )}
    </form>
  );
};

export const AddWorkCompleted: FC<Props> = ({ close, flight_work_id }) => {
  return (
    <Modal close={close}>
      <Form close={close} flight_work_id={flight_work_id} />
    </Modal>
  );
};
